import { StyleDeclarationValue } from 'aphrodite';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { CustomerImpersonationWarning } from '../../../web/components/CustomerImpersonationWarning';
import { useNavCompactToggle } from '../../../web/components/Happiness/NavFooter';
import {
	CoffeeAppGrid,
	CoffeeNavArea,
	CoffeePageArea,
	CoffeeSubBarArea,
	CoffeeWarningArea,
} from '../../shell/CoffeeAppGrid';
import { useTysonMode } from '../../contexts/tysonMode';
import { CoffeeMobileRouteGuard, useIsCoffeeMobile } from '../../shell/CoffeeMobileRouteGuard';
import { styleSheet } from '../../shell/styles';
import { CoffeeNavBar } from '../CoffeeNavBar';
import { NavUserMenuProvider } from '../../../web/components/Happiness/NavFooter/signpost';

/**
 * Mirrors main lev's `AppLayout` in shape and in the dimensions it uses, but composes Coffee's own grid:
 * the sub-bar row is Coffee's alone, and main lev's `NavBar` calls `useTextMessaging()`, which throws in
 * the aida app.
 */
export const CoffeeAppLayout = ({
	children,
	subBar,
	pageRootStyles,
	onSearch,
}: {
	children: React.ReactNode;
	/** Dialer + gamification. Omitted below `md`, where the grid row collapses to zero. */
	subBar?: React.ReactNode;
	pageRootStyles?: StyleDeclarationValue[];
	/** Replaces the nav's legacy search modal, for pages that host their own search panel. */
	onSearch?: () => void;
}) => {
	const userSession = useUserSession();
	const isMobile = useIsCoffeeMobile();
	// Compact on every page, as main lev's nav is everywhere but its Dashboard; Coffee has no such home page.
	const [showingCompactNav, toggleCompactNav] = useNavCompactToggle(true);
	const { isActive: isTysonModeActive } = useTysonMode();

	// Tyson mode repaints the page area, as the legacy `MainContainer` does on its own container.
	const pageStyles = isTysonModeActive ? [...(pageRootStyles ?? []), styleSheet.tysonBackground] : pageRootStyles;
	return (
		<NavUserMenuProvider storageKey='coffee:user-menu-moved'>
			<CoffeeAppGrid>
				<CoffeeMobileRouteGuard />
				<CoffeeNavArea>
					<CoffeeNavBar
						compactLayout={showingCompactNav}
						onSearch={onSearch}
						onToggleCompact={isMobile ? undefined : toggleCompactNav}
					/>
				</CoffeeNavArea>
				<CoffeeWarningArea>
					{/* No `ManagedAccountAccessWarning` — main lev stacks one here, but Coffee has never shown it. */}
					<CustomerImpersonationWarning fixed={false} userSession={userSession} />
				</CoffeeWarningArea>

				<CoffeeSubBarArea>{isMobile ? null : subBar}</CoffeeSubBarArea>
				<CoffeePageArea styles={pageStyles}>{children}</CoffeePageArea>
			</CoffeeAppGrid>
		</NavUserMenuProvider>
	);
};
